import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { MAP_SIDE_RAIL_WIDTH, MAP_SIDE_RAIL_MOBILE_WIDTH } from './MapSideRail';

/**
 * MapDisplayModeRail
 *
 * Pins / Hide, floating on the left edge of the map beside MapSideRail.
 *
 * This used to live inside the Map settings panel on the right, which put it
 * next to base-layer and concentration settings — things about how the map is
 * drawn. But it controls whether *venues* are visible, which belongs with the
 * venue-facing controls (Venue Criteria, Resources, Travel) on the left. Moving
 * it also means toggling pins no longer requires opening a panel.
 *
 * 'Clusters' is deliberately absent: it was retired from the UI. The clustering
 * code path in TourPlanner still exists and is simply never selected.
 */
export default function MapDisplayModeRail({
  isDarkMode = false,
  isMobile = false,
  displayMode = 'pins',
  onDisplayModeChange,
  topOffset = 12, // matches the map canvas inset; the side rail itself starts at top: 0
  visible = true,
}) {
  if (!visible || Platform.OS !== 'web') return null;

  const styles = makeStyles(isDarkMode);
  const railWidth = isMobile ? MAP_SIDE_RAIL_MOBILE_WIDTH : MAP_SIDE_RAIL_WIDTH;

  const OPTIONS = [
    { key: 'pins', label: 'Pins', icon: 'location', hint: 'Show venue pins' },
    { key: 'hidden', label: 'Hide', icon: 'eye-off-outline', hint: 'Hide venue pins' },
  ];

  return (
    <View
      style={[styles.wrap, { left: railWidth + 10, top: topOffset }]}
      pointerEvents="auto"
    >
      {OPTIONS.map((opt) => {
        const active = displayMode === opt.key;
        return (
          <TouchableOpacity
            key={opt.key}
            onPress={() => onDisplayModeChange?.(opt.key)}
            style={[styles.option, active && styles.optionActive]}
            accessibilityRole="button"
            accessibilityLabel={opt.hint}
            accessibilityState={{ selected: active }}
          >
            <Ionicons
              name={opt.icon}
              size={13}
              color={active ? '#fffffe' : (isDarkMode ? 'rgba(255,255,255,0.62)' : '#7a7a75')}
            />
            <Text style={[styles.label, active && styles.labelActive]}>
              {opt.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function makeStyles(isDarkMode) {
  return StyleSheet.create({
    wrap: {
      position: 'absolute',
      zIndex: 8, // under open rail panels, above the map canvas
      flexDirection: 'row',
      gap: 3,
      padding: 3,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#e4e4df',
      backgroundColor: isDarkMode ? 'rgba(27,27,33,0.92)' : 'rgba(255,255,254,0.94)',
      ...(Platform.OS === 'web' ? { backdropFilter: 'blur(6px)' } : {}),
    },
    option: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 6,
    },
    optionActive: {
      backgroundColor: isDarkMode ? '#f4f4f2' : '#14141a',
    },
    label: {
      fontSize: 11.5,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(255,255,255,0.72)' : '#7a7a75',
    },
    labelActive: {
      color: isDarkMode ? '#14141a' : '#fffffe',
    },
  });
}
